/**
 * Connection-tier tracking — US 10.02. Pure state machine, injectable clock.
 *
 *   Tier 0  healthy
 *   Tier 1  transient (outage < 3s)  — invisible; requests retry silently
 *   Tier 2  degraded  (3s – 8s)     — "Reconnecting…" pill; movement free,
 *                                     economy intents queue
 *   Tier 3  disconnected (≥ 8s)     — blocking overlay, all input disabled
 *
 * Signals come from real request outcomes (the 5s ephemeral flush doubles as
 * a heartbeat, so idle players still generate signals). Any success snaps
 * back to tier 0 — recovery is instant, only decay is staged.
 */

export type Tier = 0 | 1 | 2 | 3;

export const TIER2_AT_MS = 3_000;
export const TIER3_AT_MS = 8_000;

export class ConnectionTracker {
  private outageStart: number | null = null;
  private listeners = new Set<(tier: Tier) => void>();
  private lastReported: Tier = 0;

  constructor(private now: () => number = () => Date.now()) {}

  /** Call on every failed round trip (network error or 5xx). */
  reportFailure(): void {
    this.outageStart ??= this.now();
    this.emit();
  }

  /** Call on every successful round trip. */
  reportSuccess(): void {
    this.outageStart = null;
    this.emit();
  }

  tier(): Tier {
    if (this.outageStart === null) return 0;
    const outage = this.now() - this.outageStart;
    if (outage >= TIER3_AT_MS) return 3;
    if (outage >= TIER2_AT_MS) return 2;
    return 1;
  }

  /**
   * Re-evaluates on a timer tick (tiers escalate with time, not with new
   * failures — a single dead request must still reach tier 3).
   */
  poll(): void {
    this.emit();
  }

  onChange(fn: (tier: Tier) => void): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  private emit(): void {
    const t = this.tier();
    if (t === this.lastReported) return;
    this.lastReported = t;
    for (const fn of this.listeners) fn(t);
  }
}
